import React, { useState, useEffect } from "react";
import { useKeycloak } from "@react-keycloak/web";
import { useTranslation } from "react-i18next";
import Loading from "./../../common/components/loading/loading";
import { getOrganizationsByUser } from "./../../common/services/auth/userAccountOrganizationService";
import { getRolesByUser } from "./../../common/services/auth/userAccountRoleService";
import { getUserAccount } from "./../../common/services/auth/userAccountService";
import { updateUserAccountPreferences } from "./../../common/services/auth/userAccountPreferencesService";
import { getUserAccountSubscriptionsByUserAccount } from "./../../common/services/marketdata/userAccountSubscriptionService";
import ThemeableAgGrid from "./../../common/components/agGrid/ThemeableAgGrid";
import { usePermitted } from "../../common/components/permissions/permissions";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import ToggleButton from "react-bootstrap/ToggleButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";

const UserProfile = (props) => {
  const { t } = useTranslation(["auth"]);
  const { keycloak, initialized } = useKeycloak();
  const [subscriptions, setSubscriptions] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [userAccount, setUserAccount] = useState(null);

  const queryUserAccounts = usePermitted(["api-query-user-accounts"]);
  const queryOrganizations = usePermitted(["api-query-organizations"]);
  const queryRoles = usePermitted(["api-query-roles"]);
  const queryUserAccountSubscriptions = usePermitted([
    "api-query-user-account-market-subscriptions",
  ]);

  const userAccountId = keycloak.tokenParsed.OriginatingUserAccountID;
  const kcUserAccountId = keycloak.tokenParsed.sub;
  const kcLogin = keycloak.tokenParsed.preferred_username;
  const kcLastName = keycloak.tokenParsed.family_name;
  const kcFirstName = keycloak.tokenParsed.given_name;
  const kcEmail = keycloak.tokenParsed.email;

  const orgColumns = [
    { field: "name", headerName: t("Organization") },
    {
      field: "inClaims",
      headerName: t("InClaims"),
      cellRenderer: (params) => {
        const isActive = params.data?.inClaims;
        return (
          <div className="centered" style={{ width: "100%", height: "100%" }}>
            <FontAwesomeIcon
              icon={isActive ? faCheck : faTimes}
              className={isActive ? "text-success" : "text-danger"}
              style={{ fontSize: "16px" }}
            />
          </div>
        );
      },
    },
  ];

  const roleColumns = [
    { field: "name", headerName: t("Role") },
    {
      field: "inClaims",
      headerName: t("InClaims"),
      cellRenderer: (params) => {
        const isActive = params.data?.inClaims;
        return (
          <div className="centered" style={{ width: "100%", height: "100%" }}>
            <FontAwesomeIcon
              icon={isActive ? faCheck : faTimes}
              className={isActive ? "text-success" : "text-danger"}
              style={{ fontSize: "16px" }}
            />
          </div>
        );
      },
    },
  ];

  const subscriptionColumns = [
    { field: "organizationSubscriptionName", headerName: t("Subscription") },
    { field: "type", headerName: t("SubscriptionType") },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (queryUserAccounts) {
          const result = await getUserAccount(userAccountId);
          setUserAccount(result);
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, [queryUserAccounts, userAccountId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (queryOrganizations) {
          const result = await getOrganizationsByUser(userAccountId);
          setOrganizations(
            result
              .filter((x) => x.active)
              .map((organization) => ({
                ...organization,
                inClaims: keycloak.tokenParsed.group.includes(
                  organization.organizationId
                ),
              }))
          );
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, [queryOrganizations, userAccountId, keycloak.tokenParsed.group]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (queryRoles) {
          const result = await getRolesByUser(userAccountId);
          setRoles(
            result
              .filter((x) => x.active)
              .map((role) => ({
                ...role,
                inClaims: keycloak.tokenParsed.role.includes(role.name),
              }))
          );
        }
        //console.log(result); // You can use 'result' here if needed
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, [queryRoles, userAccountId, keycloak.tokenParsed.role]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (queryUserAccountSubscriptions) {
          const result = await getUserAccountSubscriptionsByUserAccount(
            userAccountId
          );
          setSubscriptions(
            result
              .filter((x) => x.active)
              .map((subscription) => ({
                ...subscription,
                type: subscription.contributor ? t("Contributor") : t("Viewer"),
              }))
          );
        }
        //console.log(result); // You can use 'result' here if needed
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, [queryUserAccountSubscriptions, userAccountId, t]); // The empty dependency array ensures that this effect runs once on component mount

  // useEffect(() => {
  //   console.log(subscriptions);
  // }, [subscriptions]);

  const roleList = keycloak.tokenParsed.role.map((role) => (
    <span style={{ fontSize: 10, color: "var(--theme-background)" }} key={role}>
      {role},{" "}
    </span>
  ));

  //  const orgList = keycloak.tokenParsed.group.map((organization) =>
  //     <li key={organization}>{organization}</li>
  //     );

  // console.log("auth_time:",new Date(keycloak.tokenParsed.auth_time))
  // console.log("exp",new Date(keycloak.tokenParsed.exp))
  // console.log("iat",new Date(keycloak.tokenParsed.iat))

  const handleSetTheme = async (theme) => {
    //This is where we'll amek the API call to persisit the setting.
    await updateUserAccountPreferences({
      userAccountID: userAccount.userAccountId,
      theme: theme,
    });

    //Call back to App.js to apply the setting in the current session.
    props.setTheme(theme);
  };

  const selectedTheme = props.theme === "light" ? "light" : "dark";

  if (!initialized) {
    return <Loading />;
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-md-12">
          <h3>{t("UserProfile")}</h3>
          <div className="row">
            <div className="col-md-2">
              <h5 className="secondary-header">{t("Login")}:</h5>
            </div>
            <div className="col-md-10">
              <h6>{(userAccount && userAccount.login) ?? kcLogin}</h6>
            </div>
          </div>
          <div className="row">
            <div className="col-md-2">
              <h5 className="secondary-header">{t("FirstName")}:</h5>
            </div>
            <div className="col-md-10">
              <span>
                {(userAccount && userAccount.firstName) ?? kcFirstName}
              </span>
            </div>
          </div>
          <div className="row">
            <div className="col-md-2">
              <h5 className="secondary-header">{t("LastName")}:</h5>
            </div>
            <div className="col-md-10">
              <span>{(userAccount && userAccount.lastName) ?? kcLastName}</span>
            </div>
          </div>
          <div className="row">
            <div className="col-md-2">
              <h5 className="secondary-header">{t("Email")}:</h5>
            </div>
            <div className="col-md-10">
              <span>{(userAccount && userAccount.email) ?? kcEmail}</span>
            </div>
          </div>
          <div className="row">
            <div className="col-md-2">
              <h5 className="secondary-header">{t("UserAccountId")}:</h5>
            </div>
            <div className="col-md-10">
              <span>
                {(userAccount && userAccount.userAccountId) ??
                  userAccountId ??
                  kcUserAccountId + "*"}
              </span>
            </div>
          </div>
          <div className="row">
            <div className="col-md-2">
              <h5 className="secondary-header">{t("Theme")}:</h5>
            </div>
            <div className="col-md-10">
              <ButtonGroup
                className="mb-2 segmented-radio-group user-profile-theme-toggle"
                aria-label={t("Theme")}
              >
                <ToggleButton
                  size="sm"
                  key={"DarkMode"}
                  id={"DarkMode"}
                  type="radio"
                  variant="outline-primary"
                  className="segmented-radio-button segmented-radio-button--theme"
                  name="theme-selector"
                  value={"dark"}
                  checked={selectedTheme === "dark"}
                  onChange={(e) => handleSetTheme(e.currentTarget.value)}
                >
                  {t("DarkMode")}
                </ToggleButton>
                <ToggleButton
                  size="sm"
                  key={"LightMode"}
                  id={"LightMode"}
                  type="radio"
                  variant="outline-primary"
                  className="segmented-radio-button segmented-radio-button--theme"
                  name="theme-selector"
                  value={"light"}
                  checked={selectedTheme === "light"}
                  onChange={(e) => handleSetTheme(e.currentTarget.value)}
                >
                  {t("LightMode")}
                </ToggleButton>
              </ButtonGroup>
            </div>
          </div>
        </div>
        {/* <div className="col-md-6">
      <h3>{t("UserAccountId")}: {keycloak.tokenParsed.OriginatingUserAccountID}</h3>
    </div>           */}
      </div>

      <div className="row">
        <div className="col-md-4">
          <h4>{t("Organizations")}</h4>
          {organizations !== null ? (
            <ThemeableAgGrid
              className="table-bold"
              columnDefs={orgColumns}
              rowData={organizations || []}
              {...(!queryOrganizations
                ? {
                    overlayNoRowsTemplate:
                      "<span>" + t("NoRetreivePermission") + "</span>",
                  }
                : {})}
            />
          ) : (
            <Loading />
          )}
        </div>
        <div className="col-md-4">
          <h4>{t("Roles")}</h4>
          {roles !== null ? (
            <ThemeableAgGrid
              className="table-bold"
              columnDefs={roleColumns}
              rowData={roles || []}
              {...(!queryRoles
                ? {
                    overlayNoRowsTemplate:
                      "<span>" + t("NoRetreivePermission") + "</span>",
                  }
                : {})}
            />
          ) : (
            <Loading />
          )}
        </div>
        <div className="col-md-4">
          <h4>{t("Subscriptions")}</h4>
          {subscriptions !== null ? (
            <ThemeableAgGrid
              className="table-bold"
              columnDefs={subscriptionColumns}
              rowData={subscriptions || []}
              {...(!queryUserAccountSubscriptions
                ? {
                    overlayNoRowsTemplate:
                      "<span>" + t("NoRetreivePermission") + "</span>",
                  }
                : {})}
            />
          ) : (
            <Loading />
          )}
        </div>
      </div>
      <div className="row">
        <div className="col-md-12">{roleList}</div>
      </div>
    </div>
  );
};

export default UserProfile;
